import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  BASE_CONTRACT_TYPES,
  CLOUDEQ_ENTITY_AMBIGUOUS_PREFIX,
  CLOUDEQ_ENTITY_NO_MATCH_NOTE,
  baseContractCandidateOf,
  baseContractIneligibility,
  type ContractDetail,
  type DecideContractTypeResponse,
  type ListBaseContractsResponse,
  type Provenanced,
  type RetrieveContractResponse,
  type SetContractLinkResponse,
} from '@clm/shared-types';

import { listBaseContracts, nominateBaseContract } from '../api/base-contracts';
import { setContractCloudeqEntity } from '../api/cloudeq-entity';
import { manageContractTags } from '../api/contract-tags';
import { retrieveContract } from '../api/contracts';
import { ApiError } from '../api/errors';
import { classifyApiError, type ApiProblemKind } from '../api/problem';
import { AiProcessingState } from '../components/AiProcessingState';
import { ContractTypeTag } from '../components/ContractTypeTag';
import { ErrorNotice } from '../components/ErrorNotice';
import { PipelineStageTracker } from '../components/PipelineStageTracker';
import { OpenContractLink } from '../components/OpenContractLink';
import { ProvenanceChip } from '../components/ProvenanceChip';
import { TagInput } from '../components/TagInput';
import { TRUST_LABELS, TRUST_TONES, processingLabel } from '../components/trust-labels';
import { mayAttempt } from '../auth/permissions';
import { useIdentity } from '../auth/useIdentity';
import { VerificationBadge, VerificationStatusLabel } from '../components/VerificationBadge';
import { useCloudeqEntities } from '../hooks/useCloudeqEntities';
import { requireSurface } from '../routes/surfaces';
import {
  BASE_NOMINATOR_ROLES,
  BASE_READER_ROLES,
  currentBaseOf,
  nominationProblemOf,
  scopedWithoutBaseCopy,
  type NominationProblem,
} from './base-msa-shared';
import { confirmTypeAndSetBase, confirmsTypeFirst } from './base-msa-confirm';
import { NominationProblemNotice } from './BaseMsaRefusal';
import { BaseDeviationSection } from './BaseDeviationSection';
import { DecisionProblemNotice } from './ContractTypeDecisionPanel';
import { ContractLinksPanel } from './ContractLinksPanel';
import { ContractTypeLine } from './ContractTypeLine';
import type { DecisionProblem } from './contract-type-decision';
import { AUTO_CONFIRMATION_KIND_COPY } from './type-proposal-copy';

/**
 * Contract record — FR-18, FR-01…FR-05, FR-09, FR-11, CR-01, NFR-07.
 *
 * The facts about one agreement, each with the provenance that makes it
 * evidence. Four things here are decisions rather than implementation:
 *
 * ## 1 · The first request asks for nothing optional
 *
 * `apps/api/src/routes/handlers.ts` answers `501` for `obligations`, `clauses`,
 * `family`, `verifications` and `notifications` — they need tables the pipeline
 * does not write yet, and the handler names them one at a time rather than
 * answering with an empty array. Asking for them would fail the whole page
 * rather than render the contract it can have. So they are not requested, and
 * each renders a one-line note saying nothing has produced them yet. **An empty
 * array is never displayed as "none"** — "we have not looked" and "there are
 * none" are different facts, and conflating them on a compliance surface is the
 * kind of small lie that becomes a finding.
 *
 * ## 2 · `full_text` is a second call, behind a disclosure
 *
 * It is the largest field in the system and the record page needs it only when
 * someone is actually reading the document. So the disclosure issues a second
 * `retrieve-contract` with `include: ['full_text']` the first time it is opened.
 * It comes from the converter and never from a model (rule 13).
 *
 * ## 3 · A `404` is one message, and stays ambiguous
 *
 * An out-of-scope key and a nonexistent one are indistinguishable **by design**
 * (`errors.ts` in @clm/shared-types): a `403` there would confirm a contract's
 * existence to someone not entitled to know it. So this surface renders exactly
 * one sentence and never becomes an existence oracle.
 *
 * ## 4 · The family walk is the server's
 *
 * Amendments inherit parties, owner, security and category from their parent
 * (§8.1 Rule 4) and scope resolution anchors on `root_contract_key`. This page
 * asks for a key and renders what it is given; assembling a family from
 * separate calls client-side would take the inheritance rule out of the query
 * predicate, which is the one place it works.
 */

type RecordPhase =
  | { readonly kind: 'loading' }
  /* `read` numbers the completed reads; the record body is keyed on it (`readsRef`). */
  | { readonly kind: 'loaded'; readonly response: RetrieveContractResponse; readonly read: number }
  | {
      readonly kind: 'problem';
      readonly problem: ApiProblemKind;
      readonly detail: string | null;
      readonly correlation_id: string | null;
    };

/** The five sections `retrieve-contract` answers 501 for, and why. */
const UNBUILT_SECTIONS: readonly { readonly label: string; readonly note: string }[] = [
  {
    label: 'Obligations',
    note: 'Nothing has extracted obligations for this contract yet. This is not an empty list — the section has not been produced, and the API answers 501 if asked for it.',
  },
  {
    label: 'Clauses',
    note: 'Nothing has segmented this contract into clause spans yet. This is not an empty list.',
  },
  {
    label: 'Agreement family',
    note: 'The family walk is not readable through this endpoint yet. When it is, it arrives server-side with the scope predicate already applied — this page will not assemble one from separate calls.',
  },
  {
    label: 'Fields awaiting verification',
    note: 'The per-contract verification list is not readable through this endpoint yet. The Verification queue surface reads the same rows across all contracts and is live.',
  },
  {
    label: 'Notifications sent',
    note: 'The per-contract alert ledger is not readable through this endpoint yet.',
  },
];

export function ContractRecord() {
  const { contract_key: contractKey } = useParams<{ contract_key: string }>();
  const surface = requireSurface('detail');

  const [phase, setPhase] = useState<RecordPhase>({ kind: 'loading' });
  const [fullText, setFullText] = useState<
    { readonly kind: 'unasked' } | { readonly kind: 'loading' } | { readonly kind: 'ready'; readonly text: string | null } | { readonly kind: 'problem'; readonly problem: ApiProblemKind }
  >({ kind: 'unasked' });

  const abortRef = useRef<AbortController | null>(null);
  const textAbortRef = useRef<AbortController | null>(null);
  /* CLM-77 — bumped after a tag save so the record is re-fetched, never patched locally. */
  const [reload, setReload] = useState(0);
  /*
   * CLM-120 — counts completed reads. `Loaded` is keyed on it so a re-read always
   * starts the body afresh (an open dialog closes, its local state resets) and the
   * receipts held above are what remain. Without the key that held only when React
   * committed the interim `loading` phase; a read that answered first was batched
   * with it, and the old body, its Links dialog still open, stayed on screen.
   */
  const readsRef = useRef(0);
  /*
   * CLM-110 — the last type decision made from this record. Held here, above the
   * re-fetch it triggers, so the receipt is still on screen when the new record
   * lands; cleared when the page moves to another contract.
   */
  const [typeReceipt, setTypeReceipt] = useState<DecideContractTypeResponse | null>(null);
  /*
   * CLM-118 (R-118-13) — what Confirm type and set as base did to the base, held above the re-fetch for
   * the same reason: the type decision re-reads the record, and a refused nomination must still say so.
   */
  const [baseOutcome, setBaseOutcome] = useState<BaseOutcome | null>(null);
  /* CLM-120 (R-120-19) — the last link saved from this record, held above the re-fetch for the same reason. */
  const [linksReceipt, setLinksReceipt] = useState<SetContractLinkResponse | null>(null);
  useEffect(() => {
    setTypeReceipt(null);
    setBaseOutcome(null);
    setLinksReceipt(null);
  }, [contractKey]);

  useEffect(() => {
    if (contractKey === undefined) return;

    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;
    setPhase({ kind: 'loading' });
    setFullText({ kind: 'unasked' });

    void (async () => {
      try {
        // No `include`: see the module note, decision 1.
        const response = await retrieveContract(contractKey, undefined, controller.signal);
        if (controller.signal.aborted) return;
        readsRef.current += 1;
        setPhase({ kind: 'loaded', response, read: readsRef.current });
      } catch (error) {
        if (controller.signal.aborted) return;
        setPhase({
          kind: 'problem',
          problem: classifyApiError(error),
          detail: error instanceof ApiError ? error.message : null,
          correlation_id: error instanceof ApiError ? error.correlation_id : null,
        });
      }
    })();

    return () => {
      controller.abort();
    };
  }, [contractKey, reload]);

  useEffect(
    () => () => {
      textAbortRef.current?.abort();
    },
    [],
  );

  function loadFullText() {
    if (contractKey === undefined) return;
    if (fullText.kind !== 'unasked') return;

    const controller = new AbortController();
    textAbortRef.current?.abort();
    textAbortRef.current = controller;
    setFullText({ kind: 'loading' });

    void (async () => {
      try {
        const response = await retrieveContract(contractKey, ['full_text'], controller.signal);
        if (controller.signal.aborted) return;
        setFullText({ kind: 'ready', text: response.full_text });
      } catch (error) {
        if (controller.signal.aborted) return;
        setFullText({ kind: 'problem', problem: classifyApiError(error) });
      }
    })();
  }

  if (contractKey === undefined) {
    return (
      <>
        <Header label={surface.label} summary={surface.summary} />
        <div className="card">
          <h3>No contract selected</h3>
          <p>
            Open one from <strong>Search</strong> or <strong>Needs attention</strong> — those
            surfaces return keys the API has already scoped to you, so a key that reaches this page
            is one you may read.
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <Header label={surface.label} summary={surface.summary} />

      {phase.kind === 'loading' ? (
        <div className="card" role="status" aria-busy="true">
          <h3>
            Loading <code>{contractKey}</code>
          </h3>
          <p>The API is resolving the key against your scope.</p>
        </div>
      ) : null}

      {phase.kind === 'problem' ? (
        <ErrorNotice
          kind={phase.problem}
          detail={phase.detail}
          correlation_id={phase.correlation_id}
        />
      ) : null}

      {phase.kind === 'loaded' ? (
        <Loaded
          key={phase.read}
          response={phase.response}
          fullText={fullText}
          onOpenFullText={loadFullText}
          onTagsSaved={() => setReload((n) => n + 1)}
          typeReceipt={typeReceipt}
          onTypeDecided={(response) => {
            setTypeReceipt(response);
            setBaseOutcome(null);
            setReload((n) => n + 1);
          }}
          baseOutcome={baseOutcome}
          onConfirmedAndSet={(response, outcome) => {
            setTypeReceipt(response);
            setBaseOutcome(outcome);
            setReload((n) => n + 1);
          }}
          linksReceipt={linksReceipt}
          onLinkSaved={(response) => {
            setLinksReceipt(response);
            /* An amendment takes its agreement's type (R-120-5): an earlier decision's receipt no longer describes it. */
            if (response.link === 'amends') setTypeReceipt(null);
            setReload((n) => n + 1);
          }}
        />
      ) : null}
    </>
  );
}

function Header({ label, summary }: { readonly label: string; readonly summary: string }) {
  return (
    <div className="hd">
      <div>
        <h2>{label}</h2>
        <p>{summary}</p>
      </div>
    </div>
  );
}

/**
 * CLM-77 — the contract's tags: chips for everyone, *Edit tags* for a
 * contributor or admin (`contract:update` — `mayAttempt` is a display hint
 * only; the API gate decides, and a sign-in that names no role still sees the
 * button, D-82-1). Save posts the **replacement set** and asks the
 * page to re-fetch the record: what is shown afterwards is what the database
 * holds, never the browser's guess.
 */
/** The roles the gateway grants `contract:update` to — a display hint, never the gate. */
const TAG_EDITOR_ROLES: readonly string[] = ['contributor', 'admin'];

function TagsLine({ contract, onSaved }: { readonly contract: ContractDetail; readonly onSaved: () => void }) {
  const { identity } = useIdentity();
  const mayEdit = mayAttempt(identity, TAG_EDITOR_ROLES) && !contract.is_read_only;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<readonly string[]>(contract.tags);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<{ readonly kind: ApiProblemKind; readonly detail: string | null; readonly correlation_id: string | null } | null>(null);

  function open() {
    setDraft(contract.tags);
    setProblem(null);
    setEditing(true);
  }

  async function save() {
    setSaving(true);
    setProblem(null);
    try {
      await manageContractTags({ contract_key: contract.contract_key, tags: [...draft] });
      setEditing(false);
      onSaved();
    } catch (error) {
      setProblem({
        kind: classifyApiError(error),
        detail: error instanceof ApiError ? error.message : null,
        correlation_id: error instanceof ApiError ? error.correlation_id : null,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="tags-edit" data-testid="contract-tags">
      <span className="fl">
        <strong>Tags:</strong>
        {contract.tags.length === 0 ? (
          <span className="mut">none</span>
        ) : (
          <ul className="tags-ro" aria-label="Tags">
            {contract.tags.map((tag) => (
              <li key={tag} className="tag">
                {tag}
              </li>
            ))}
          </ul>
        )}
        {mayEdit && !editing ? (
          <button type="button" className="btn sm qt" onClick={open}>
            Edit tags
          </button>
        ) : null}
      </span>
      {editing ? (
        <div role="group" aria-label="Edit tags">
          <TagInput id="record-tags" label="Tags" value={draft} onChange={setDraft} disabled={saving} />
          <span className="fl">
            <button type="button" className="btn sm pri" disabled={saving} onClick={() => void save()}>
              {saving ? 'Saving…' : 'Save tags'}
            </button>
            <button type="button" className="btn sm qt" disabled={saving} onClick={() => setEditing(false)}>
              Cancel
            </button>
            <span className="mut">
              The set you save replaces the tags on this contract and is written with one audit row.
            </span>
          </span>
          {problem === null ? null : (
            <ErrorNotice kind={problem.kind} detail={problem.detail} correlation_id={problem.correlation_id} />
          )}
        </div>
      ) : null}
    </div>
  );
}

/**
 * CLM-91 — which cloudEQ legal entity signed (D-91-1, D-91-4). The badge is
 * the API's value with its provenance in the title: `text_scan` is the
 * deterministic in-tenant scan of the converted document at the store step (no
 * model, no verification row — a lookup, not an AI fact); `admin` is an
 * override; *Unassigned* explains itself from `cloudeq_entity_scan_note`. The
 * select posts `set-contract-cloudeq-entity` and asks the page to re-fetch —
 * what is shown afterwards is what the database holds. Never disabled by role:
 * the API is the gate (CLM-82); a 403 renders the notice and the select stays
 * usable. Hidden on a read-only (superseded) record like every other write.
 */
function cloudeqEntityTitle(contract: ContractDetail): string {
  if (contract.cloudeq_entity_source === 'admin') return 'Set by an admin';
  if (contract.cloudeq_entity_source === 'text_scan') return 'Set by the in-tenant text scan of the converted document';
  const note = contract.cloudeq_entity_scan_note;
  if (note === CLOUDEQ_ENTITY_NO_MATCH_NOTE) return 'The in-tenant scan found no cloudEQ legal entity named in this document — set it here';
  if (note !== null && note.startsWith(CLOUDEQ_ENTITY_AMBIGUOUS_PREFIX)) {
    return `The in-tenant scan found more than one cloudEQ legal entity named (${note.slice(CLOUDEQ_ENTITY_AMBIGUOUS_PREFIX.length).split(',').join(', ')}) — set the signing one here`;
  }
  return 'Not scanned yet — the document has not been converted';
}

/**
 * CLM-114 (R-114-5) — under each vendor party, the vendor-list cards that
 * identify it: "Vendor no. *X* · cloudEQ *entity*". The references are read
 * inside the API's scoped parties statement; the entity's name comes from the
 * registry, falling back to its key while that has not loaded. Display only.
 */
function VendorReferencesLine({ parties }: { readonly parties: ContractDetail['parties'] }) {
  const entities = useCloudeqEntities();
  const nameOf = (key: string): string => entities.find((entity) => entity.entity_key === key)?.display_name ?? key;
  return (
    <ul className="vendor-refs" aria-label="Vendor-list references">
      {parties
        .filter((party) => party.vendor_references.length > 0)
        .map((party) => (
          <li key={party.party_key} className="mut">
            {party.cached_display_name}:{' '}
            {party.vendor_references.map((reference, index) => (
              <span key={`${reference.cloudeq_entity_key}-${reference.vendor_no}`}>
                {index > 0 ? '; ' : null}
                Vendor no. <code>{reference.vendor_no}</code> · {nameOf(reference.cloudeq_entity_key)}
              </span>
            ))}
          </li>
        ))}
    </ul>
  );
}

function CloudeqEntityBlock({ contract, onSaved }: { readonly contract: ContractDetail; readonly onSaved: () => void }) {
  const entities = useCloudeqEntities();
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<{ readonly kind: ApiProblemKind; readonly detail: string | null; readonly correlation_id: string | null } | null>(null);
  const name =
    contract.cloudeq_entity_key === null
      ? 'Unassigned'
      : (entities.find((entity) => entity.entity_key === contract.cloudeq_entity_key)?.display_name ?? contract.cloudeq_entity_key);

  async function set(entityKey: string | null) {
    setSaving(true);
    setProblem(null);
    try {
      await setContractCloudeqEntity({ contract_key: contract.contract_key, entity_key: entityKey });
      onSaved();
    } catch (error) {
      setProblem({
        kind: classifyApiError(error),
        detail: error instanceof ApiError ? error.message : null,
        correlation_id: error instanceof ApiError ? error.correlation_id : null,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card" data-testid="contract-cloudeq-entity">
      <h3>cloudEQ entity</h3>
      <span className="fl">
        <span className="tag" title={cloudeqEntityTitle(contract)}>
          {name}
        </span>
        {contract.is_read_only ? null : (
          <select
            aria-label={`cloudEQ entity for ${contract.contract_key}`}
            value={contract.cloudeq_entity_key ?? 'unassigned'}
            disabled={saving}
            onChange={(event) => {
              const value = event.target.value;
              void set(value === 'unassigned' ? null : value);
            }}
          >
            <option value="unassigned">Unassigned</option>
            {entities.map((entity) => (
              <option key={entity.entity_key} value={entity.entity_key}>
                {entity.display_name}
              </option>
            ))}
            {contract.cloudeq_entity_key !== null && !entities.some((entity) => entity.entity_key === contract.cloudeq_entity_key) ? (
              <option value={contract.cloudeq_entity_key}>{contract.cloudeq_entity_key}</option>
            ) : null}
          </select>
        )}
        <span className="mut">
          {contract.cloudeq_entity_source === 'admin'
            ? 'An admin value is kept by every later scan; choose Unassigned to let the next scan place it again.'
            : 'Derived from the converted text inside the tenant — never from the model. An admin choice is written with one audit row.'}
        </span>
      </span>
      {problem === null ? null : <ErrorNotice kind={problem.kind} detail={problem.detail} correlation_id={problem.correlation_id} />}
    </div>
  );
}

/**
 * CLM-111 — the base-MSA badge and action on the record (FR-37).
 *
 * **The badge is display only.** It reads the nomination, never the contract:
 * being the base changes nothing about how the contract behaves (R-111-1), so
 * nothing else on this page moves when it appears.
 *
 * **The lookup is asked only of a sign-in that may read it** —
 * `mayAttempt(identity, ['admin', 'legal_reviewer'])`, `clause:review_match`'s
 * holders. A failure of that lookup (a 403 for a role the SPA could not name,
 * or anything else) hides the badge and the action quietly: it is an
 * auxiliary read, and this page's own problem banner is about the contract,
 * not about the base.
 *
 * **The action is an admin's, on an eligible contract that is not the base**
 * — `baseContractIneligibility`, the rule the API refuses on — and it is the
 * same POST as the Base MSA page, with the same
 * `expected_current_nomination_id`: "Set as base MSA" when there is none,
 * "Replace base MSA with this contract" when there is one. Two clicks, never
 * one: the confirm names the version being replaced. An amendment is never
 * offered — it carries its parent's type, so the shared rule reads its
 * `lineage.amends_contract_key` (`baseContractCandidateOf`).
 *
 * **Under a practices scope, "no base" is "none visible".** A base whose
 * contract is outside the admin's scope is not listed (R-111-7), so the
 * confirm does not promise v1 there, and a `base_changed` from that state says
 * a base may be outside the scope — the re-read would answer "none" again.
 * Refusals the API names are `NominationProblemNotice`'s own banner, never
 * `ErrorNotice`'s copy for the kind (the Base MSA page's rule).
 *
 * **A nomination moves the base for the whole record**, not only this line:
 * `onBaseChanged` is called whenever this line learns the base moved — its own
 * nomination succeeded, or the API said `base_changed` — so the CLM-112
 * section below re-reads its current base and readiness too.
 *
 * **An MSA confirmed because the file name and the AI agree** (CLM-118,
 * R-118-13) is offered **Confirm type and set as base**: the Admin's type
 * decision (the record's own `seen`), then the nomination — two audited acts,
 * each by its own route. The record then re-reads, so the type line shows the
 * decision's receipt and this line the badge; a refused nomination is held by
 * the page across that re-read (`baseOutcome`) and says the type stays
 * confirmed.
 */
function BaseMsaLine({
  contract,
  onBaseChanged,
  outcome,
  onConfirmedAndSet,
}: {
  readonly contract: ContractDetail;
  readonly onBaseChanged: () => void;
  readonly outcome: BaseOutcome | null;
  readonly onConfirmedAndSet: (response: DecideContractTypeResponse, outcome: BaseOutcome) => void;
}) {
  const { identity } = useIdentity();
  const mayRead = mayAttempt(identity, BASE_READER_ROLES);
  const mayNominate = mayAttempt(identity, BASE_NOMINATOR_ROLES);
  const [bases, setBases] = useState<ListBaseContractsResponse | null>(null);
  const [nonce, setNonce] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<NominationProblem | null>(null);
  const [decisionProblem, setDecisionProblem] = useState<DecisionProblem | null>(null);

  useEffect(() => {
    if (!mayRead) return;
    const controller = new AbortController();
    void listBaseContracts(controller.signal).then(
      (response) => {
        if (!controller.signal.aborted) setBases(response);
      },
      () => {
        /* Hidden quietly — see the note above. */
        if (!controller.signal.aborted) setBases(null);
      },
    );
    return () => {
      controller.abort();
    };
  }, [mayRead, contract.contract_key, nonce]);

  if (!mayRead || bases === null) return outcome === null ? null : <BaseOutcomeNotice outcome={outcome} />;

  const isBase = currentBaseOf(bases, contract.contract_key);
  /* The enabled type this contract could be the base for, if any (MSA only today). */
  const type = BASE_CONTRACT_TYPES.find((candidate) => candidate === contract.contract_type) ?? null;
  const current = type === null ? null : (bases.types.find((entry) => entry.contract_type === type)?.current ?? null);
  /* CLM-118 — an MSA the file name and the AI agree on is offered once an administrator confirms its type. */
  const typeFirst = mayNominate && isBase === null && type !== null && confirmsTypeFirst(type, contract);
  const offer =
    mayNominate &&
    isBase === null &&
    type !== null &&
    (baseContractIneligibility(type, baseContractCandidateOf(contract)) === null || typeFirst);
  const scopedWithoutBase = bases.scope_kind === 'practices' && current === null;

  if (isBase === null && !offer) return outcome === null ? null : <BaseOutcomeNotice outcome={outcome} />;

  async function nominate() {
    if (type === null) return;
    setSaving(true);
    setProblem(null);
    try {
      await nominateBaseContract({
        contract_type: type,
        contract_key: contract.contract_key,
        expected_current_nomination_id: current?.nomination_id ?? null,
      });
      setConfirming(false);
      setNonce((n) => n + 1);
      onBaseChanged();
    } catch (error) {
      const failure = nominationProblemOf(error, { scopedWithoutBase });
      setProblem(failure);
      if (failure.refusal?.code === 'base_changed') {
        setNonce((n) => n + 1);
        onBaseChanged();
      }
    } finally {
      setSaving(false);
    }
  }

  /** CLM-118 (R-118-13) — the Admin's type decision, then the nomination; the page holds the outcome. */
  async function confirmThenNominate() {
    if (type === null) return;
    setSaving(true);
    setProblem(null);
    setDecisionProblem(null);
    try {
      const result = await confirmTypeAndSetBase({
        type,
        contract,
        expected_current_nomination_id: current?.nomination_id ?? null,
        scopedWithoutBase,
      });
      if (result.kind === 'decision_failed') {
        setDecisionProblem(result.problem);
        return;
      }
      setConfirming(false);
      onConfirmedAndSet(
        result.decision,
        result.kind === 'done' ? { nominated: true, problem: null } : { nominated: false, problem: result.problem },
      );
      onBaseChanged();
    } finally {
      setSaving(false);
    }
  }

  /* Only rendered when `offer`, which implies `type` is an enabled base type. */
  const typeName = type ?? contract.contract_type;
  const label = typeFirst
    ? current === null
      ? `Confirm type and set as base ${typeName}`
      : `Confirm type and replace base ${typeName} with this contract`
    : current === null
      ? `Set as base ${typeName}`
      : `Replace base ${typeName} with this contract`;

  return (
    <div className="fl" data-testid="contract-base-msa">
      {isBase === null ? null : (
        <span
          className="tag acc"
          title="Nominated on the Base MSA page. Display only — being the base changes nothing about this contract."
        >
          Base {isBase.contract_type} · v{isBase.nomination.version_no}
        </span>
      )}
      {offer && !confirming ? (
        <button type="button" className="btn sm qt" onClick={() => setConfirming(true)}>
          {label}
        </button>
      ) : null}
      {offer && confirming ? (
        <span className="fl" role="group" aria-label={label}>
          <span className="mut">
            {current === null
              ? scopedWithoutBase
                ? scopedWithoutBaseCopy(typeName)
                : `This contract becomes the base ${typeName} (v1).`
              : `This replaces ${current.contract_key} (v${String(current.version_no)}) as the base ${typeName}; the earlier version stays in the history.`}{' '}
            {/* CLM-121 (R-121-14) — the kind that settled it, never 1a's words for a policy approval. */}
            {typeFirst
              ? `Its type was confirmed because ${
                  contract.contract_type_status.auto_confirmation?.kind === 'auto_approval_policy'
                    ? 'auto-approval for AI-decided types approved it'
                    : AUTO_CONFIRMATION_KIND_COPY.file_name_and_ai_agree
                }; confirming records your check of the type (${typeName}) first, then the nomination.`
              : 'The contract itself is not changed.'}
          </span>
          <button
            type="button"
            className="btn sm pri"
            disabled={saving}
            onClick={() => void (typeFirst ? confirmThenNominate() : nominate())}
          >
            {saving ? 'Saving…' : 'Confirm'}
          </button>
          <button type="button" className="btn sm qt" disabled={saving} onClick={() => setConfirming(false)}>
            Cancel
          </button>
        </span>
      ) : null}
      {problem === null ? null : <NominationProblemNotice problem={problem} />}
      {decisionProblem === null ? null : <DecisionProblemNotice problem={decisionProblem} onReload={onBaseChanged} />}
      {outcome === null ? null : <BaseOutcomeNotice outcome={outcome} />}
    </div>
  );
}

/** CLM-118 (R-118-13) — what Confirm type and set as base did to the base; the type line shows the decision. */
interface BaseOutcome {
  readonly nominated: boolean;
  readonly problem: NominationProblem | null;
}

function BaseOutcomeNotice({ outcome }: { readonly outcome: BaseOutcome }) {
  if (outcome.nominated) {
    return (
      <p role="status" data-testid="contract-base-confirmed-and-set">
        Type confirmed, then set as the base.
      </p>
    );
  }
  return (
    <>
      <p className="mut" data-testid="contract-base-type-confirmed-only">
        The type is confirmed by you; the nomination was not recorded.
      </p>
      {outcome.problem === null ? null : <NominationProblemNotice problem={outcome.problem} />}
    </>
  );
}

function Loaded({
  response,
  fullText,
  onOpenFullText,
  onTagsSaved,
  typeReceipt,
  onTypeDecided,
  baseOutcome,
  onConfirmedAndSet,
  linksReceipt,
  onLinkSaved,
}: {
  readonly response: RetrieveContractResponse;
  readonly fullText:
    | { readonly kind: 'unasked' }
    | { readonly kind: 'loading' }
    | { readonly kind: 'ready'; readonly text: string | null }
    | { readonly kind: 'problem'; readonly problem: ApiProblemKind };
  readonly onOpenFullText: () => void;
  readonly onTagsSaved: () => void;
  readonly typeReceipt: DecideContractTypeResponse | null;
  readonly onTypeDecided: (response: DecideContractTypeResponse) => void;
  readonly baseOutcome: BaseOutcome | null;
  readonly onConfirmedAndSet: (response: DecideContractTypeResponse, outcome: BaseOutcome) => void;
  readonly linksReceipt: SetContractLinkResponse | null;
  readonly onLinkSaved: (response: SetContractLinkResponse) => void;
}) {
  const contract = response.contract;
  /* CLM-111 → CLM-112: bumped when the base moves, so the deviation section re-reads what it compares against. */
  const [baseGeneration, setBaseGeneration] = useState(0);
  /* CLM-120 (R-120-13) — the type line's "Link as an amendment…" opens the link dialog on amends; cleared when it closes. */
  const [linksIntent, setLinksIntent] = useState<'amends' | null>(null);

  return (
    <>
      <div className="card">
        <h3>
          <code>{contract.contract_key}</code>{' '}
          {/* CLM-89 (R-89-1) — the same control the verify panel has. */}
          <OpenContractLink url={contract.documents.sharepoint_item_url} />
        </h3>
        <p>
          {contract.parties
            .map((party) => `${party.cached_display_name} (${party.party_role})`)
            .join(', ')}{' '}
          {/* CLM-110 (R-110-23) — the type with its provisional tag until an administrator confirms it. */}·{' '}
          <ContractTypeTag
            type={contract.contract_type}
            confirmation={contract.contract_type_status.confirmation}
            autoKind={contract.contract_type_status.auto_confirmation?.kind ?? null}
          />{' '}
          ·{' '}
          {/* CLM-99 — the primary party's Dynamics billing client id; an explicit
           * "Not billable" state, never a blank cell that could read as a loading
           * failure (R-99-1 item 4). */}
          {(() => {
            const primary = contract.parties.find((party) => party.is_primary) ?? null;
            return primary === null || primary.billing_client_id === null ? (
              <span className="mut">Not billable</span>
            ) : (
              <>
                billing id <code>{primary.billing_client_id}</code>
              </>
            );
          })()}
        </p>
        {/* CLM-114 (R-114-5) — mounted only when a party carries a vendor-list reference, so a record
         * without one asks the entity registry for nothing more than it did. */}
        {contract.parties.some((party) => party.vendor_references.length > 0) ? (
          <VendorReferencesLine parties={contract.parties} />
        ) : null}
        <div className="fl">
          {/*
           * TD-04: lifecycle and processing are two axes and are never
           * collapsed. A contract can be legally Active and governing while its
           * renewal date is still awaiting verification; showing one without the
           * other tells the user something false.
           *
           * CLM-108 (R-108-14, D-2.1-4): the pipeline axis prints through
           * `processingLabel`, whose end state reads "Settled" — an
           * auto-approved contract reaches it too — and the trust axis
           * (`data_trust`, B-02) gets its own tag beside it. That tag is the
           * only place this record says "Verified" about the contract, and only
           * when every field was settled by a person.
           */}
          <span className="tag up">Lifecycle: {contract.lifecycle_status}</span>
          <span className="tag acc">Processing: {processingLabel(contract.processing_status)}</span>
          <span className={`tag ${TRUST_TONES[contract.data_trust]}`} data-testid="contract-trust">
            Trust: {TRUST_LABELS[contract.data_trust]}
          </span>
          {/* CLM-85 (R-85-1) — conversion passed with a quality caveat: attention (`acc`), not a failure (`bad`). */}
          {contract.conversion_caveat === 'tables_missing' ? (
            <span
              className="tag acc"
              title="The converter found no table in this document although text density was fine. Rate-card and key-term values may have been read from flattened prose — check them against the PDF."
            >
              Tables not detected
            </span>
          ) : null}
          {contract.processing_restricted ? (
            <span className="tag bad">Processing restricted — no LLM (§4C)</span>
          ) : null}
          {contract.is_read_only ? (
            <span className="tag out">Read-only — superseded (§8.1 Rule 3)</span>
          ) : null}
        </div>
        {/* CLM-110 — how the type was decided and whether it is settled; an administrator decides it here (D-110-2). */}
        <ContractTypeLine
          contract={contract}
          receipt={typeReceipt}
          onDecided={onTypeDecided}
          onReload={onTagsSaved}
          onLinkAsAmendment={() => setLinksIntent('amends')}
        />
        {/* CLM-77 — the contract's own tags (R-77-1); readers see chips, contributors may edit. */}
        <TagsLine contract={contract} onSaved={onTagsSaved} />
        {/* CLM-111 — the base-MSA badge (display only) and, for an admin, Set / Replace. */}
        <BaseMsaLine
          contract={contract}
          onBaseChanged={() => setBaseGeneration((n) => n + 1)}
          outcome={baseOutcome}
          onConfirmedAndSet={onConfirmedAndSet}
        />
        {/* CLM-120 — the contract's links for every reader; an administrator fixes them here (R-120-19). */}
        <ContractLinksPanel
          contract={contract}
          onChanged={onLinkSaved}
          receipt={linksReceipt}
          openOn={linksIntent}
          onClosed={() => setLinksIntent(null)}
        />
        {/* CLM-70 — the five-step pipeline tracker (D-70-5); the tags above stay (TD-04). */}
        <PipelineStageTracker contract={contract} />
        {contract.is_read_only ? (
          <p className="mut">
            This contract has been superseded, so it is read-only. There are no write controls on
            this page in any case; the API and a database trigger both reject the write regardless
            of what a browser offers.
          </p>
        ) : null}
      </div>

      {/* CLM-112 — compare an MSA with the base MSA (Admin and Legal; offered, never forced — R-112-16). */}
      <BaseDeviationSection contract={contract} baseGeneration={baseGeneration} />

      {/* CLM-91 — the signing cloudEQ entity, before the AI block: it is an in-tenant lookup, not an AI fact. */}
      <CloudeqEntityBlock contract={contract} onSaved={onTagsSaved} />

      <div className="card">
        <h3>AI processing</h3>
        <AiProcessingState ai={contract.ai_processing} />
      </div>

      <div className="card">
        <h3>Dates</h3>
        <div className="frows">
          <FieldRow name="Effective date" field={contract.dates.effective_date} />
          <FieldRow name="Execution date" field={contract.dates.execution_date} />
          <FieldRow name="Expiry date" field={contract.dates.expiry_date} />
          <FieldRow name="Termination date" field={contract.dates.termination_date} />
          <FieldRow name="Renewal notice date" field={contract.dates.renewal_notice_date} />
          <FieldRow name="Renewal notice days" field={contract.dates.renewal_notice_days} />
          <FieldRow name="Auto-renew" field={contract.dates.auto_renew} />
        </div>
      </div>

      <div className="card">
        <h3>Key terms</h3>
        <div className="frows">
          <FieldRow name="Contract value" field={contract.key_terms.contract_value} />
          <FieldRow name="Currency" field={contract.key_terms.currency} />
          <FieldRow name="Payment terms" field={contract.key_terms.payment_terms} />
          <FieldRow name="Liability cap" field={contract.key_terms.liability_cap} />
          <FieldRow name="Indemnity scope" field={contract.key_terms.indemnity_scope} />
          <FieldRow name="Insurance requirements" field={contract.key_terms.insurance_requirements} />
          <FieldRow name="Governing law" field={contract.key_terms.governing_law} />
          <FieldRow name="Jurisdiction" field={contract.key_terms.jurisdiction} />
          <FieldRow name="Exclusivity" field={contract.key_terms.exclusivity} />
        </div>
      </div>

      {/* CLM-109 (R-109-15) — admin-defined fields, from their field_verification rows: display only, not
          searchable, never a notification or dashboard input. Absent for a contract extracted before any existed. */}
      {contract.additional_terms.length === 0 ? null : (
        <div className="card" data-testid="contract-additional-terms">
          <h3>Additional fields</h3>
          <div className="frows">
            {contract.additional_terms.map((term) => (
              <div className="frow-f" key={term.field_name}>
                <span className="fname">
                  <code>{term.field_key}</code>
                </span>
                <span className="fval" data-absent={term.value === null ? 'true' : 'false'}>
                  {term.value ?? 'No value — see the verification queue for why'}
                </span>
                <span className="fprov">
                  <VerificationStatusLabel status={term.verification_status} />
                </span>
              </div>
            ))}
          </div>
          {/* CLM-108 (R-108-16) — an auto-approved value is the extraction's: the policy settles it, nobody rewrites it. */}
          <p className="mut">
            Defined by an administrator, not by the governed schema. A value is the reviewer&rsquo;s once a person
            settles it, and the extraction&rsquo;s until then or when it was auto-approved by policy; it lives only in
            the verification record and is not searchable.
          </p>
        </div>
      )}

      <div className="card">
        <h3>Personal data</h3>
        <div className="frows">
          <FieldRow
            name="Contains personal data"
            field={contract.personal_data.contains_personal_data}
          />
        </div>
        <p className="mut">
          {contract.personal_data.categories.length === 0
            ? 'No personal-data categories were recorded.'
            : `Categories: ${contract.personal_data.categories.join(', ')}`}
        </p>
      </div>

      <div className="card">
        <h3>Plain-language summary</h3>
        <div className="frows">
          <FieldRow name="Summary" field={contract.plain_summary} />
        </div>
      </div>

      <div className="card">
        <h3>Document text</h3>
        <details className="disclose" onToggle={onOpenFullText}>
          <summary>Show document text</summary>
          {fullText.kind === 'loading' ? (
            <p role="status">Fetching the converted document text…</p>
          ) : null}
          {fullText.kind === 'problem' ? <ErrorNotice kind={fullText.problem} /> : null}
          {fullText.kind === 'ready' ? (
            fullText.text === null ? (
              <p className="unbuilt">
                There is no converted text for this contract. That is also the case when
                conversion took the PDF-native fallback path — the same fact that makes the
                fallback an FR-29 compliance decision rather than a technical one.
              </p>
            ) : (
              <pre className="doctext">{fullText.text}</pre>
            )
          ) : null}
          <p className="mut">
            Fetched on demand: it is the largest field in the system. It comes from the converter,
            never from a model — asking a model to re-transcribe twenty pages costs five times as
            much and introduces errors.
          </p>
        </details>
      </div>

      <div className="card">
        <h3>Not produced yet</h3>
        <p>
          These sections are not requested, because the API answers <code>501</code> for each of
          them today rather than an empty array. What follows is what has not happened — not a
          statement that there is nothing to find.
        </p>
        <div className="frows">
          {UNBUILT_SECTIONS.map((section) => (
            <div className="frow-f" key={section.label}>
              <span className="fname">{section.label}</span>
              <p className="unbuilt">{section.note}</p>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

/**
 * One provenanced field: the value, and the provenance that makes it evidence.
 *
 * `provenanced()` wraps every extractable value in the contract precisely so a
 * bare fact cannot be rendered — if you find yourself wanting to unwrap one to
 * save a row here, that is the control working.
 */
function FieldRow({
  name,
  field,
}: {
  readonly name: string;
  readonly field: Provenanced<string | number | boolean | null>;
}) {
  const absent = field.value === null;
  // Booleans read as Yes/No, never as `true`/`false`. "Contains personal data:
  // false" is machine output on a surface people read for a compliance answer.
  const shown =
    typeof field.value === 'boolean' ? (field.value ? 'Yes' : 'No') : String(field.value);
  return (
    <div className="frow-f">
      <span className="fname">{name}</span>
      <span className="fval" data-absent={absent ? 'true' : 'false'}>
        {absent ? 'The contract does not state one' : shown}
      </span>
      <span className="fprov">
        <ProvenanceChip provenance={field.provenance} />
        <VerificationBadge verification={field.provenance.verification} />
      </span>
    </div>
  );
}
